import React, { useMemo, useState } from 'react';
import { Select } from '../../components/ui/Field';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import Modal from '../../components/overlays/Modal';
import { TREND_RANGES } from '../../helpers/dates';
import './aichat.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-IN');

/**
 * Short enough to sit above a bar when thirty of them share the width. Empty for a
 * day with no sales -- a row of "₹0" is noise, and the missing bar already says it.
 */
function compactInr(value) {
  const n = Number(value || 0);
  if (n === 0) return '';
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${Math.round(n)}`;
}

/* A bar covers a period, so its tooltip says which. */
const BUCKET_PREFIX = { week: 'Week of', month: 'Month of' };

/** '2026-08-05' -> '05 Aug 2026'. The series dates are already IST calendar days. */
function formatDay(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* `today` is the IST calendar day (00:00–23:59 IST), not a rolling 24 hours --
   see istDayRange in helpers/dates. The rolling windows keep `days`. */
export const DAY_PERIODS = [
  // `compare` names the baseline the delta is measured against, so a percentage is
  // never shown without saying what it is a percentage of.
  { value: 'today', label: 'Today', compare: 'vs yesterday' },
  { value: '24h', label: 'Last 24 hours', days: 1, compare: 'vs prev 24h' },
  { value: '7d', label: 'Last 7 days', days: 7, compare: 'vs prev 7d' },
  { value: '30d', label: 'Last 30 days', days: 30, compare: 'vs prev 30d' },
  { value: 'custom', label: 'Custom range…', compare: 'vs prev period' },
];

/** "1 Aug, 12:00 am – 7 Aug, 4:43 pm" for the delta's hover text. */
function describePrevious(previous, what) {
  if (!previous) return undefined;
  const at = (iso) =>
    iso
      ? new Date(iso).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Kolkata',
        })
      : '?';
  return `Compared with ${at(previous.start)} – ${at(previous.end)} IST (${what})`;
}

/**
 * The daily bars. Lives in the trend modal rather than on the page: at a readable
 * height it was the tallest thing on the screen, and it answers a question you ask
 * occasionally, not one you need answered on every visit.
 */
function TrendChart({ series, bucket = 'day', onSelectDay }) {
  const [hover, setHover] = useState(null);

  const chart = useMemo(() => {
    if (series.length === 0) return { bars: [], max: 0 };
    const max = Math.max(...series.map((d) => d.total), 1);
    return {
      max,
      bars: series.map((d) => ({
        date: d.date,
        // One height for the stack, split inside it by each part's share, so the
        // day's label can sit directly on top of the column.
        pct: (d.total / max) * 100,
      })),
    };
  }, [series]);

  const active = hover != null ? series[hover] : null;

  /* Past roughly forty columns a "₹1.2k" is wider than the bar it belongs to and the
     labels collide into a grey smear. The tooltip still gives the exact figure. */
  const showValues = series.length <= 40;

  /* The tooltip is positioned inside the plot, never outside it. Hanging it above the
     plot put it beyond the modal body, which clips overflow -- so it was invisible,
     and it grew the body's scroll height enough to summon a scrollbar, which shifted
     the whole chart sideways on hover. */
  const tipLeft =
    hover == null || chart.bars.length === 0
      ? 0
      : Math.min(88, Math.max(12, ((hover + 0.5) / chart.bars.length) * 100));
  // Sits just above its own bar, but never so high that it leaves the plot.
  const tipBottom = hover == null ? 0 : Math.min(58, (chart.bars[hover]?.pct || 0) + 4);

  const axis = useMemo(() => {
    if (series.length === 0) return [];
    // Monthly buckets want a month and year; a bare "01 Aug" reads as a single day.
    const fmt = (iso) => {
      const d = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(d.getTime())) return iso;
      return bucket === 'month'
        ? d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
        : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    };
    return [
      fmt(series[0].date),
      fmt(series[Math.floor(series.length / 2)].date),
      fmt(series[series.length - 1].date),
    ];
  }, [series, bucket]);

  return (
    <>
      <div
        className="sv-ai-chart__plot is-large"
        onMouseLeave={() => setHover(null)}
      >
        {chart.bars.map((bar, i) => {
          const day = series[i];
          // The drill-down filters the table to one IST day, so it only makes sense
          // on daily bars. A weekly bar would silently filter to its Monday.
          const canDrill = day.total > 0 && bucket === 'day';
          return (
            <button
              type="button"
              key={bar.date}
              className={`sv-ai-chart__bar ${hover === i ? 'is-hover' : ''} ${
                canDrill ? 'is-clickable' : ''
              }`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              // A day with nothing sold would filter the table to an empty list.
              onClick={canDrill ? () => onSelectDay?.(bar.date) : undefined}
              aria-label={`${formatDay(bar.date)}: ${inr(day.total)} from ${
                day.flow_count
              } flows and ${day.credit_count} credit packs${
                canDrill ? '. Show these users' : ''
              }`}
            >
              {/* Every column is labelled with what it made, so the chart reads
                  without hovering. Blank on a day with no sales rather than "₹0". */}
              {showValues && (
                <span className="sv-ai-chart__bar-value sv-mono">{compactInr(day.total)}</span>
              )}
              <span className="sv-ai-chart__stack" style={{ height: `${bar.pct}%` }}>
                <span
                  className="sv-ai-chart__seg is-credit"
                  style={{ flexGrow: day.credit_earning || 0 }}
                />
                <span
                  className="sv-ai-chart__seg is-flow"
                  style={{ flexGrow: day.flow_earning || 0 }}
                />
              </span>
            </button>
          );
        })}

        {active && (
          <div
            className="sv-ai-chart__tip"
            style={{ left: `${tipLeft}%`, bottom: `${tipBottom}%` }}
            role="status"
          >
            <div className="sv-ai-chart__tip-date">
              {bucket === 'day' ? formatDay(active.date) : `${BUCKET_PREFIX[bucket]} ${formatDay(active.date)}`}
            </div>
            <div className="sv-ai-chart__tip-row">
              <i className="sv-ai-chart__dot is-flow" />
              <span>Flows</span>
              <b className="sv-mono">{inr(active.flow_earning)}</b>
              <span className="sv-ai-chart__tip-count sv-mono">×{active.flow_count}</span>
            </div>
            <div className="sv-ai-chart__tip-row">
              <i className="sv-ai-chart__dot is-credit" />
              <span>Credits</span>
              <b className="sv-mono">{inr(active.credit_earning)}</b>
              <span className="sv-ai-chart__tip-count sv-mono">×{active.credit_count}</span>
            </div>
            <div className="sv-ai-chart__tip-row is-total">
              <span>Total</span>
              <b className="sv-mono">{inr(active.total)}</b>
            </div>
            {active.total > 0 && bucket === 'day' && (
              <div className="sv-ai-chart__tip-hint">Click to show these users</div>
            )}
          </div>
        )}

        {chart.bars.length === 0 && (
          <div className="sv-ai-chart__empty">No purchases in this range</div>
        )}
      </div>

      {axis.length === 3 && (
        <div className="sv-ai-chart__axis sv-mono">
          <span>{axis[0]}</span>
          <span>{axis[1]}</span>
          <span>{axis[2]}</span>
        </div>
      )}

      <div className="sv-ai-chart__legend">
        <span>
          <i className="sv-ai-chart__dot is-flow" /> Flow earnings
        </span>
        <span>
          <i className="sv-ai-chart__dot is-credit" /> Credit earnings
        </span>
      </div>
    </>
  );
}

/**
 * AI Chat overview block.
 *
 * Two independent windows, deliberately:
 *  - `metrics` is scoped to the selected day period and drives the tiles and the
 *    message summary in the header.
 *  - `trend` is pinned to the last 30 days and drives the trend tile and its modal.
 *
 * Within each, every figure is derived from that one response, so a headline can never
 * disagree with the bars beneath it. Nothing here recomputes a total independently.
 */
export default function AiOverview({
  metrics,
  trend,
  loading,
  dayPeriod,
  onDayPeriodChange,
  dayCustom,
  onDayCustomChange,
  onSelectDay,
  trendRange,
  onTrendRangeChange,
  trendCustom,
  onTrendCustomChange,
  trendBucket,
  trendLoading,
}) {
  const [trendOpen, setTrendOpen] = useState(false);
  const totals = metrics?.totals;
  const series = useMemo(() => trend?.series || [], [trend]);

  /* One delta per quantity: the transaction tile compares counts, the trend tile
     compares rupees. They used to share a single figure derived from earnings, so the
     count tile reported a change it was not showing. */
  const delta = metrics?.delta?.transaction_count;
  const deltaTitle = describePrevious(
    metrics?.delta?.previous,
    `${num(metrics?.delta?.previous?.transaction_count)} transactions`
  );
  const trendDelta = trend?.delta?.total_earning;
  const trendDeltaTitle = describePrevious(
    trend?.delta?.previous,
    inr(trend?.delta?.previous?.total_earning)
  );
  const comparePhrase =
    DAY_PERIODS.find((p) => p.value === dayPeriod)?.compare || 'vs previous period';
  const trendTotal = trend?.totals?.total_earning;
  const messages = metrics?.messages;
  const rangeLabel =
    TREND_RANGES.find((r) => r.value === trendRange)?.label || 'Last 30 days';

  /* Tiles reload when the day period changes. `v` shows a placeholder in place of a
     tile value only while the period-scoped request is in flight. */
  const v = (node) => (loading ? <SkeletonBlock width={64} height={18} /> : node);

  return (
    <div className="sv-ai-overview">
      <div className="sv-ai-overview__head">
        <h2 className="sv-card__title">Overview</h2>

        {/* Message counts read as context on the heading rather than as their own
            band across the page, which is a row of vertical space for three numbers. */}
        {messages && (
          <div className="sv-ai-messages">
            <span>
              <strong>{num(messages.user_messages)}</strong> user messages
            </span>
            <span className="sv-ai-messages__rule" />
            <span>
              <strong>{num(messages.assistant_messages)}</strong> Vani replies
            </span>
            <span className="sv-ai-messages__rule" />
            <span>{messages.replies_per_message} replies per message</span>
          </div>
        )}

        <div className="sv-ai-overview__period">
          <span>Day period</span>
          <Select
            options={DAY_PERIODS.map((p) => ({ value: p.value, label: p.label }))}
            value={dayPeriod}
            onChange={(e) => onDayPeriodChange(e.target.value)}
          />

          {/* Both tiles and table read the same window, so picking a range here
              scopes the whole screen. Nothing is requested until both ends are set. */}
          {dayPeriod === 'custom' && (
            <span className="sv-ai-range__custom">
              <input
                type="date"
                className="sv-ai-range__date"
                aria-label="Period start"
                max={dayCustom?.end || undefined}
                value={dayCustom?.start || ''}
                onChange={(e) => onDayCustomChange({ ...dayCustom, start: e.target.value })}
              />
              <span className="sv-ai-range__to">to</span>
              <input
                type="date"
                className="sv-ai-range__date"
                aria-label="Period end"
                min={dayCustom?.start || undefined}
                value={dayCustom?.end || ''}
                onChange={(e) => onDayCustomChange({ ...dayCustom, end: e.target.value })}
              />
            </span>
          )}
        </div>
      </div>

      <div className="sv-ai-grid">
        <div className="sv-ai-tile is-inverted">
          <div className="sv-ai-tile__label">Total purchase value</div>
          <div className="sv-ai-tile__value">{v(inr(totals?.total_earning))}</div>
          <div className="sv-ai-tile__context">
            <span>{v(`${inr(totals?.flow_earning)} flows`)}</span>
            <span className="sv-ai-tile__sep">+</span>
            <span>{v(`${inr(totals?.credit_earning)} credits`)}</span>
          </div>
        </div>

        <div className="sv-card sv-ai-tile">
          <div className="sv-ai-tile__label">Transactions</div>
          <div className="sv-ai-tile__value-row">
            <span className="sv-ai-tile__value">{v(num(totals?.transaction_count))}</span>
            {delta != null && (
              <span
                className={`sv-ai-tile__delta ${delta >= 0 ? 'is-up' : 'is-down'}`}
                title={deltaTitle}
              >
                {delta >= 0 ? '+' : '−'}
                {Math.abs(delta)}%
              </span>
            )}
            {delta != null && <span className="sv-ai-tile__vs">{comparePhrase}</span>}
          </div>
          <div className="sv-ai-tile__context">
            <span>{v(`${num(totals?.flow_count)} flows`)}</span>
            <span className="sv-ai-tile__sep">+</span>
            <span>{v(`${num(totals?.credit_count)} credits`)}</span>
          </div>
        </div>

        <div className="sv-card sv-ai-tile">
          <div className="sv-ai-tile__label">Flows</div>
          <div className="sv-ai-split">
            <div>
              <div className="sv-ai-split__value">{v(num(totals?.flow_count))}</div>
              <div className="sv-ai-split__label">Purchased</div>
            </div>
            <div>
              <div className="sv-ai-split__value">{v(inr(totals?.flow_earning))}</div>
              <div className="sv-ai-split__label">Earnings</div>
            </div>
          </div>
        </div>

        <div className="sv-card sv-ai-tile">
          <div className="sv-ai-tile__label">Credits</div>
          <div className="sv-ai-split">
            <div>
              <div className="sv-ai-split__value">{v(num(totals?.credit_count))}</div>
              <div className="sv-ai-split__label">Purchased</div>
            </div>
            <div>
              <div className="sv-ai-split__value">{v(inr(totals?.credit_earning))}</div>
              <div className="sv-ai-split__label">Earnings</div>
            </div>
          </div>
        </div>

        {/* The headline and delta stay on the page; only the bars move behind a click.
            The range is named because this tile ignores the day period. */}
        <button
          type="button"
          className="sv-card sv-ai-tile sv-ai-trend"
          onClick={() => setTrendOpen(true)}
        >
          <div className="sv-ai-tile__label">
            Transaction trend
            <span className="sv-ai-trend__range">{rangeLabel}</span>
          </div>
          <div className="sv-ai-tile__value-row">
            <span className="sv-ai-tile__value">{inr(trendTotal)}</span>
            {trendDelta != null && (
              <span
                className={`sv-ai-tile__delta ${trendDelta >= 0 ? 'is-up' : 'is-down'}`}
                title={trendDeltaTitle}
              >
                {trendDelta >= 0 ? '+' : '−'}
                {Math.abs(trendDelta)}%
              </span>
            )}
            {trendDelta != null && <span className="sv-ai-tile__vs">vs prev period</span>}
          </div>
          <span className="sv-ai-trend__link">
            View chart <i className="bx bx-right-arrow-alt" aria-hidden="true" />
          </span>
        </button>
      </div>

      <Modal
        open={trendOpen}
        onClose={() => setTrendOpen(false)}
        title={`Transaction trend — ${rangeLabel.toLowerCase()}`}
        size="xl"
        hideCancel
        footer={<span />}
      >
        <div className="sv-ai-range">
          <Select
            options={TREND_RANGES}
            value={trendRange}
            onChange={(e) => onTrendRangeChange(e.target.value)}
          />

          {trendRange === 'custom' && (
            <span className="sv-ai-range__custom">
              <input
                type="date"
                className="sv-ai-range__date"
                aria-label="Range start"
                value={trendCustom?.start || ''}
                onChange={(e) =>
                  onTrendCustomChange({ ...trendCustom, start: e.target.value })
                }
              />
              <span className="sv-ai-range__to">to</span>
              <input
                type="date"
                className="sv-ai-range__date"
                aria-label="Range end"
                value={trendCustom?.end || ''}
                onChange={(e) => onTrendCustomChange({ ...trendCustom, end: e.target.value })}
              />
            </span>
          )}

          {/* Say when bars stop being days, so a tall column is not misread as one
              enormous day. */}
          {trendBucket && trendBucket !== 'day' && (
            <span className="sv-ai-range__note">one bar = one {trendBucket}</span>
          )}

          <span className="sv-ai-range__total sv-mono">{inr(trendTotal)} total</span>
        </div>

        {trendLoading && <SkeletonBlock width="100%" height={420} radius={10} />}

        {!trendLoading && (
        <TrendChart
          series={series}
          bucket={trendBucket}
          onSelectDay={(date) => {
            // Close on the way out: the point of the click is to look at the table
            // underneath, which the modal is covering.
            setTrendOpen(false);
            onSelectDay?.(date);
          }}
        />
        )}
      </Modal>
    </div>
  );
}
